import { useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import { Input } from './Field'
import { Modal } from './Modal'

export interface ConfirmOptions {
  danger?: boolean
  confirmLabel?: string
  cancelLabel?: string
  description?: string
}

export interface AlertOptions {
  confirmLabel?: string
  description?: string
}

export interface PromptOptions {
  title?: string
  defaultValue?: string
  placeholder?: string
  confirmLabel?: string
  cancelLabel?: string
  description?: string
}

type Pending =
  | {
      kind: 'confirm'
      title: string
      danger: boolean
      confirmLabel?: string
      cancelLabel?: string
      description?: string
      resolve(ok: boolean): void
    }
  | {
      kind: 'alert'
      title: string
      confirmLabel?: string
      description?: string
      resolve(): void
    }
  | {
      kind: 'prompt'
      title: string
      defaultValue?: string
      placeholder?: string
      confirmLabel?: string
      cancelLabel?: string
      description?: string
      resolve(val: string | null): void
    }

let current: Pending | null = null
const listeners = new Set<() => void>()
const emit = () => { for (const l of listeners) l() }

function dismissCurrent(): void {
  if (!current) return
  const p = current
  current = null
  emit()
  if (p.kind === 'confirm') p.resolve(false)
  else if (p.kind === 'prompt') p.resolve(null)
  else p.resolve()
}

/**
 * Promise-based confirmation popup replacing `window.confirm` (styled, accessible, a bottom sheet on
 * phones). `danger` (default) styles the confirm button red. Resolves false on Cancel/Escape/backdrop.
 */
export function confirmDialog(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
  dismissCurrent()
  return new Promise(resolve => {
    current = {
      kind: 'confirm',
      title: message,
      danger: opts.danger ?? true,
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      description: opts.description,
      resolve,
    }
    emit()
  })
}

/**
 * Promise-based alert popup replacing `window.alert` (styled, accessible).
 */
export function alertDialog(message: string, opts: AlertOptions = {}): Promise<void> {
  dismissCurrent()
  return new Promise(resolve => {
    current = {
      kind: 'alert',
      title: message,
      confirmLabel: opts.confirmLabel,
      description: opts.description,
      resolve,
    }
    emit()
  })
}

/**
 * Promise-based text prompt replacing `window.prompt` (styled, accessible with Input and form submit).
 * Resolves null on Cancel/Escape/backdrop, or the entered string on Submit.
 */
export function promptDialog(message: string, opts: PromptOptions = {}): Promise<string | null> {
  dismissCurrent()
  return new Promise(resolve => {
    current = {
      kind: 'prompt',
      title: opts.title ?? message,
      description: opts.title ? message : opts.description,
      defaultValue: opts.defaultValue,
      placeholder: opts.placeholder,
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      resolve,
    }
    emit()
  })
}

function ConfirmModal({ pending, onClose }: { pending: Extract<Pending, { kind: 'confirm' }>; onClose(ok: boolean): void }) {
  const { t } = useTranslation()
  return (
    <Modal
      open
      title={pending.title}
      onClose={() => onClose(false)}
      description={pending.description}
      footer={<>
        <Button variant="secondary" onClick={() => onClose(false)}>{pending.cancelLabel ?? t('common.cancel')}</Button>
        <Button variant={pending.danger ? 'danger' : 'primary'} onClick={() => onClose(true)}>{pending.confirmLabel ?? t('common.confirm')}</Button>
      </>}
    >
      <p className="text-sm text-ink-400">
        {pending.description ? null : pending.danger ? t('common.cannotUndo') : t('common.confirmHint')}
      </p>
    </Modal>
  )
}

function AlertModal({ pending, onClose }: { pending: Extract<Pending, { kind: 'alert' }>; onClose(): void }) {
  const { t } = useTranslation()
  return (
    <Modal
      open
      title={pending.title}
      onClose={onClose}
      description={pending.description}
      footer={
        <Button variant="primary" onClick={onClose}>{pending.confirmLabel ?? t('common.ok')}</Button>
      }
    >
      {pending.description ? <p className="text-sm text-ink-400">{pending.description}</p> : null}
    </Modal>
  )
}

function PromptModal({ pending, onClose }: { pending: Extract<Pending, { kind: 'prompt' }>; onClose(val: string | null): void }) {
  const { t } = useTranslation()
  const [val, setVal] = useState(pending.defaultValue ?? '')

  return (
    <Modal
      open
      title={pending.title}
      onClose={() => onClose(null)}
      description={pending.description}
      footer={<>
        <Button variant="secondary" onClick={() => onClose(null)}>{pending.cancelLabel ?? t('common.cancel')}</Button>
        <Button variant="primary" onClick={() => onClose(val)}>{pending.confirmLabel ?? t('common.save')}</Button>
      </>}
    >
      <form onSubmit={e => { e.preventDefault(); onClose(val) }} className="flex flex-col gap-2">
        <Input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          placeholder={pending.placeholder}
          aria-label={typeof pending.title === 'string' ? pending.title : undefined}
        />
      </form>
    </Modal>
  )
}

export function ConfirmHost() {
  const p = useSyncExternalStore(l => { listeners.add(l); return () => listeners.delete(l) }, () => current)
  if (!p) return null

  if (p.kind === 'prompt') {
    return (
      <PromptModal
        key={p.title}
        pending={p}
        onClose={val => {
          const resolve = p.resolve
          current = null
          emit()
          resolve(val)
        }}
      />
    )
  }

  if (p.kind === 'alert') {
    return (
      <AlertModal
        key={p.title}
        pending={p}
        onClose={() => {
          const resolve = p.resolve
          current = null
          emit()
          resolve()
        }}
      />
    )
  }

  return (
    <ConfirmModal
      key={p.title}
      pending={p}
      onClose={ok => {
        const resolve = p.resolve
        current = null
        emit()
        resolve(ok)
      }}
    />
  )
}
